import {
  queryOptions,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Megaphone, Plus } from "lucide-react"
import { useState } from "react"

import { DeleteServiceDialog } from "@/components/changelog/delete-service-dialog"
import { ServiceCard } from "@/components/changelog/service-card"
import { Button } from "@/components/ui/button"
import { listServices } from "@/http/changelog/services"
import type { TChangelogService } from "@/http/changelog/types"

const servicesQueryOptions = (orgSlug: string) =>
  queryOptions({
    queryKey: ["changelog-services", orgSlug],
    queryFn: async () => {
      const response = await listServices({
        organizationSlug: orgSlug,
      })
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

export const Route = createFileRoute(
  "/_authenticated/$orgSlug/changelog/",
)({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      servicesQueryOptions(params.orgSlug),
    )
    return {
      title: "Changelog",
      path: `/${params.orgSlug}/changelog`,
    }
  },
  component: ChangelogServicesPage,
})

function ChangelogServicesPage() {
  const { orgSlug } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data } = useSuspenseQuery(
    servicesQueryOptions(orgSlug),
  )

  const [deleteTarget, setDeleteTarget] =
    useState<TChangelogService | null>(null)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone className="h-6 w-6" />
            <h1 className="text-2xl font-bold md:text-3xl">
              Changelog
            </h1>
          </div>
          <p className="text-muted-foreground mt-2">
            Gerencie os serviços e suas atualizações
            públicas.
          </p>
        </div>
        <Button
          onClick={() =>
            navigate({
              to: "/$orgSlug/changelog/services/new",
              params: { orgSlug },
            })
          }
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo serviço
        </Button>
      </div>

      {data.services.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <Megaphone className="text-muted-foreground mb-4 h-12 w-12" />
          <h2 className="text-lg font-semibold">
            Nenhum serviço cadastrado
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Crie seu primeiro serviço para começar a
            publicar atualizações.
          </p>
          <Button
            className="mt-4"
            onClick={() =>
              navigate({
                to: "/$orgSlug/changelog/services/new",
                params: { orgSlug },
              })
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            Criar serviço
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.services.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              orgSlug={orgSlug}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      <DeleteServiceDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        service={deleteTarget}
        organizationSlug={orgSlug}
        onSuccess={() =>
          queryClient.invalidateQueries({
            queryKey: [
              "changelog-services",
              orgSlug,
            ],
          })
        }
      />
    </div>
  )
}
